import { type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useFamilyFocusMode } from './useFamilyFocusMode';
import './ProfileAccountDeletion.css';

function AccountDeletionDialog({
  onClose,
  onRequest,
  stage,
  returnFocusRef,
}: {
  onClose: () => void;
  onRequest: () => void;
  stage: 'confirm' | 'request';
  returnFocusRef: RefObject<HTMLButtonElement>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useFamilyFocusMode(dialogRef, returnFocusRef);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const trigger = returnFocusRef.current;
    const { body, documentElement } = document;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      width: body.style.width,
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
    };
    const previousRootOverflow = documentElement.style.overflow;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
    const bodyPaddingRight = parseFloat(window.getComputedStyle(body).paddingRight) || 0;

    // Keep the underlying profile still, including on mobile Safari.
    Object.assign(body.style, {
      position: 'fixed',
      top: `${-scrollY}px`,
      left: `${-scrollX}px`,
      width: '100%',
      overflow: 'hidden',
      paddingRight: `${bodyPaddingRight + scrollbarWidth}px`,
    });
    documentElement.style.overflow = 'hidden';
    dialog.showModal();
    cancelRef.current?.focus();

    return () => {
      dialog.close();
      Object.assign(body.style, previousBodyStyles);
      documentElement.style.overflow = previousRootOverflow;
      window.scrollTo(scrollX, scrollY);
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [returnFocusRef]);

  return (
    <dialog
      ref={dialogRef}
      id="profile-account-deletion-dialog"
      className="profile-account-deletion-dialog"
      aria-modal="true"
      aria-labelledby="profile-account-deletion-title"
      aria-describedby="profile-account-deletion-warning"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={(event) => {
        // StrictMode closes and reopens this native dialog during effect replay.
        // Its queued close event must not dismiss the already reopened dialog.
        if (!event.currentTarget.open) onClose();
      }}
    >
      <button
        type="button"
        className="profile-account-deletion-dialog__close"
        aria-label="Schliessen"
        onClick={onClose}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="m6 6 12 12M6 18 18 6" strokeLinecap="round" />
        </svg>
      </button>
      <h2 id="profile-account-deletion-title">{stage === 'confirm' ? 'Konto endgültig löschen?' : 'Kontolöschung beantragen'}</h2>
      <div id="profile-account-deletion-warning" className="profile-account-deletion-dialog__warning">
        {stage === 'confirm' ? <>
        <p>
          Diese Aktion ist endgültig. Ihr RiviDent Konto sowie die darin gespeicherten Daten und Einstellungen werden dauerhaft gelöscht und können nicht wiederhergestellt werden.
        </p>
        <p>
          Ihre Patientendaten in der Praxis und bereits vereinbarte Termine bleiben bestehen.
        </p>
        <p>
          Das Löschen Ihres Kontos gilt nicht als Terminabsage. Möchten Sie einen Termin absagen, tun Sie dies bitte vor der Kontolöschung direkt über RiviDent.
        </p>
        </> : <>
          <p>Die automatische Kontolöschung ist derzeit noch nicht verfügbar.</p>
          <p>Wenn Sie Ihr RiviDent-Konto löschen möchten, senden Sie uns bitte eine E-Mail an{' '}
            <a href="mailto:admin@rivident.ch">admin@rivident.ch</a>. Wir kümmern uns um Ihr Anliegen.</p>
        </>}
      </div>
      <div className="profile-account-deletion-dialog__actions">
        <button ref={cancelRef} type="button" className="profile-account-deletion-dialog__cancel" onClick={onClose}>
          {stage === 'confirm' ? 'Abbrechen' : 'Schliessen'}
        </button>
        {stage === 'confirm' ? <button
          type="button"
          className="profile-account-deletion-dialog__confirm"
          onClick={onRequest}
        >
          Konto endgültig löschen
        </button> : <a className="profile-account-deletion-dialog__confirm"
          href={`mailto:admin@rivident.ch?subject=${encodeURIComponent('Antrag auf Löschung meines RiviDent-Kontos')}`}>
          E-Mail senden
        </a>}
      </div>
    </dialog>
  );
}

export function ProfileAccountDeletion() {
  const [stage, setStage] = useState<'confirm' | 'request' | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  useFamilyFocusMode(rootRef);

  return (
    <div ref={rootRef} className="profile-account-deletion">
      <button
        ref={triggerRef}
        type="button"
        className="profile-account-deletion__link"
        aria-haspopup="dialog"
        aria-controls={stage ? 'profile-account-deletion-dialog' : undefined}
        aria-expanded={stage !== null}
        onClick={() => setStage('confirm')}
      >
        Konto löschen
      </button>
      {stage && <AccountDeletionDialog key={stage} stage={stage} onRequest={() => setStage('request')}
        onClose={() => setStage(null)} returnFocusRef={triggerRef} />}
    </div>
  );
}
